'use client';

import { m, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

import {
  CalendarClockIcon,
  FileSparkleIcon,
  MicrophoneIcon,
  PenSparkleIcon,
  PhoneIcon,
  StarIcon,
  UserGroupIcon,
} from '@heidi/growth';

function BreatheWrap({
  children,
  delay = 0,
  enabled,
  className,
}: {
  children: ReactNode;
  delay?: number;
  enabled: boolean;
  className?: string;
}) {
  if (!enabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <m.div
      className={className}
      animate={{ scale: [1, 1.08, 1] }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay,
      }}
    >
      {children}
    </m.div>
  );
}

/**
 * Decorative, pixel-locked illustration for the mobile completion step.
 *
 * Arbitrary Tailwind values (`left-[...]`, `top-[...]`, `size-[...]`, etc.) are
 * intentional here: each position and size matches the Figma spec exactly.
 * Substituting the spacing scale would distort the composition. Treat this
 * component as an icon/illustration, not a layout primitive.
 *
 * Figma: https://www.figma.com/design/edgIeajMOqpAxGbdqcq2oZ/Scribe-led-sign-up---onboarding?node-id=5960-60154
 */
export function MobileCompletionIllustration() {
  const prefersReducedMotion = useReducedMotion();
  const animate = !prefersReducedMotion;

  return (
    <div
      aria-hidden
      className="relative mx-auto h-[241px] w-full max-w-[349px] shrink-0 lg:hidden"
    >
      <div className="absolute left-[111px] top-[75px] z-10 flex h-[144px] w-[131px] flex-col gap-2 rounded-2xl bg-surface p-4 shadow-lg">
        <div className="flex flex-col gap-2">
          <div className="h-2 w-[65px] rounded-full bg-text-tertiary" />
          <div className="h-2 w-[81px] rounded-full bg-black/15" />
          <div className="h-2 w-full rounded-full bg-black/15" />
          <div className="h-2 w-[93px] rounded-full bg-black/15" />
        </div>
        <div className="flex flex-col gap-2">
          <div className="h-2 w-[65px] rounded-full bg-text-tertiary" />
          <div className="h-2 w-[90px] rounded-full bg-black/15" />
          <div className="h-2 w-full rounded-full bg-black/15" />
        </div>
      </div>

      <BreatheWrap
        delay={0}
        enabled={animate}
        className="absolute left-[151px] top-0 size-[52px]"
      >
        <StarIcon className="size-full text-text-primary" />
      </BreatheWrap>
      <BreatheWrap
        delay={1.0}
        enabled={animate}
        className="absolute left-[44px] top-[45px] size-[43px]"
      >
        <MicrophoneIcon className="size-full text-text-primary" />
      </BreatheWrap>
      <BreatheWrap
        delay={1.5}
        enabled={animate}
        className="absolute left-0 top-[121px] size-[44px]"
      >
        <CalendarClockIcon className="size-full text-text-primary" />
      </BreatheWrap>
      <BreatheWrap
        delay={2.0}
        enabled={animate}
        className="absolute left-[44px] top-[197px] size-[44px]"
      >
        <PhoneIcon className="size-full text-text-primary" />
      </BreatheWrap>
      <BreatheWrap
        delay={2.5}
        enabled={animate}
        className="absolute left-[267px] top-[45px] size-[44px]"
      >
        <PenSparkleIcon className="size-full text-text-primary" />
      </BreatheWrap>
      <BreatheWrap
        delay={3.0}
        enabled={animate}
        className="absolute left-[305px] top-[121px] size-[44px]"
      >
        <FileSparkleIcon className="size-full text-text-primary" />
      </BreatheWrap>
      <BreatheWrap
        delay={3.5}
        enabled={animate}
        className="absolute left-[267px] top-[197px] size-[44px]"
      >
        <UserGroupIcon className="size-full text-text-primary" />
      </BreatheWrap>
    </div>
  );
}
